import { EDataBaseType, IColumnTypes, IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import LocalStorageService from "../../../auth/local-storage-service";

/**
 * The column types `GET /typesystem/columntypes` offers, read the way the table editor needs them.
 *
 * The list holds two kinds: the primitives (`schemaName` null) and one type per table of the
 * project's schemas — dbo's and, in a solution, every member project's — which a column takes to
 * reference that table (`schemaName` set, `kuikaTypeName` the table's name). One name and one match
 * serve the label, the model and the checkmark, so the three can never disagree.
 */

/** The key of the primitives' group; a schema's group is keyed `schema-<name>`. */
export const PRIMITIVE_COLUMN_TYPE_GROUP: string = "primitive";

/** One tab of the type picker: the primitives, or one schema's tables. */
export interface IColumnTypeGroup {
  key: string;
  label: string;
  types: IColumnTypes[];
}

/** The model types `schemaName` as a string, but the primitives come back with null. */
const schemaOf = (type: IColumnTypes): string | null => (type.schemaName as string | null | undefined) ?? null;

/** A type that references a table rather than holding a value of its own. */
export const isTableReferenceType = (type: IColumnTypes): boolean => schemaOf(type) !== null;

/**
 * What a column of this type stores as its `kuikaDataType`, and what the picker calls it:
 * `schema.Table` for a table reference, the bare name otherwise. A table the backend returns with
 * an empty schema stays bare, as the column model has always stored it.
 */
export const columnTypeName = (type: IColumnTypes): string => {
  const schema: string | null = schemaOf(type);
  return schema ? `${schema}.${type.kuikaTypeName}` : type.kuikaTypeName;
};

/** Ordinal, so the tab order is the same on every machine, whatever its locale. */
const compareOrdinal = (a: string, b: string): number => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

/** Where a schema's tab goes: dbo, then the project's own, then the rest by name, then the schema-less. */
const schemaRank = (schema: string, projectSchema: string): number => {
  if (schema === "") return 3;
  const lowered: string = schema.toLowerCase();
  if (lowered === "dbo") return 0;
  if (lowered === projectSchema) return 1;
  return 2;
};

/**
 * The type picker's tabs: Primitive, then dbo, the project's schema, and every other schema by
 * name. Only groups that have types are returned, each keeping the order the server listed them.
 */
export const groupColumnTypes = (
  types: IColumnTypes[],
  projectSchema: string | null | undefined
): IColumnTypeGroup[] => {
  const primitives: IColumnTypes[] = [];
  const bySchema: Map<string, IColumnTypes[]> = new Map<string, IColumnTypes[]>();
  types.forEach((type: IColumnTypes): void => {
    const schema: string | null = schemaOf(type);
    if (schema === null) {
      primitives.push(type);
      return;
    }
    const tables: IColumnTypes[] | undefined = bySchema.get(schema);
    if (tables) tables.push(type);
    else bySchema.set(schema, [type]);
  });

  const project: string = (projectSchema ?? "").toLowerCase();
  const schemas: string[] = Array.from(bySchema.keys()).sort((a: string, b: string): number => {
    const byRank: number = schemaRank(a, project) - schemaRank(b, project);
    return byRank !== 0 ? byRank : compareOrdinal(a, b);
  });

  const groups: IColumnTypeGroup[] = [];
  if (primitives.length > 0) {
    groups.push({ key: PRIMITIVE_COLUMN_TYPE_GROUP, label: "Primitive", types: primitives });
  }
  schemas.forEach((schema: string): void => {
    groups.push({ key: `schema-${schema}`, label: schema || "Tables", types: bySchema.get(schema) ?? [] });
  });
  return groups;
};

/**
 * The type a column is on. The primitives are tried first, so a table named like one (`app.Guid`)
 * never takes a Guid column's checkmark; a table reference matches on schema and name whatever
 * the database's Guid is called (`uniqueidentifier`, `uuid`, `RAW(16)`). A schema spelled in
 * another case is found too — SQL Server compares them case-insensitively and Postgres folds them.
 */
export const resolveColumnType = (
  types: IColumnTypes[],
  kuikaDataType: string | null | undefined
): IColumnTypes | undefined => {
  if (!kuikaDataType) return undefined;
  const primitive: IColumnTypes | undefined = types.find(
    (type: IColumnTypes): boolean => !isTableReferenceType(type) && type.kuikaTypeName === kuikaDataType
  );
  if (primitive) return primitive;
  const exact: IColumnTypes | undefined = types.find(
    (type: IColumnTypes): boolean => isTableReferenceType(type) && columnTypeName(type) === kuikaDataType
  );
  if (exact) return exact;
  const lowered: string = kuikaDataType.toLowerCase();
  return types.find(
    (type: IColumnTypes): boolean => isTableReferenceType(type) && columnTypeName(type).toLowerCase() === lowered
  );
};

/** Oracle's NVARCHAR2 ceiling, and what `OracleConnector.GetSqlType` emits for a Max column. */
const ORACLE_MAX_NVARCHAR2_LENGTH: number = 2000;

/**
 * Whether a column's Size reads `Max` rather than a number. `-1` is the stored sentinel, which
 * SQL Server round-trips but Oracle cannot: a Max column is emitted as NVARCHAR2(2000) and read
 * back as 2000. Matching 2000 is display only — rewriting `maxLength` would alter the DDL and
 * disable the Browse Data length guard, which ignores lengths <= 0.
 */
export const isMaxColumnSize = (column: IDatabaseColumnModel | undefined): boolean => {
  const maxLength: number = Number(column?.maxLength);
  if (!Number.isFinite(maxLength)) return false;
  if (maxLength === -1) return true;
  const isOracleWorkspace: boolean = LocalStorageService.getCurrentDomainManagedDbType() === EDataBaseType.Oracle;
  return isOracleWorkspace && column?.kuikaDataType === "String" && maxLength === ORACLE_MAX_NVARCHAR2_LENGTH;
};
